import { PureComponent } from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';

import { Button, Link } from '@codex/basics';
import CompareProduct from './presentionalComponents/CompareProduct';

import uiLocale from '../../translations/UILocale';
import {
  convertDefaultChannelsAvailabilityForSponsoredProduct,
  getDefaultDeliveriesAndCollectionForProduct
} from '../../dataConverters/defaultChannelsAvailabilitiesConverter';

const MAX_NUMBER = 4;

const CompareButton = styled(Button)`
  display: flex;
  align-items: center;
  height: 40px;
  width: auto;
  padding: 0 20px;
  font-size: 14px;
  color: ${(props) => (props.disabled ? '' : '#4855a0')};
  border: 2px solid #4855a0;
  border-radius: 3px;
  margin: 0 20px;
  outline: 0;
  transition: background 0.2s ease;
  white-space: nowrap;
  @media only screen and (max-width: 1024px) {
    margin: 0 10px;
  } ;
`;

const ClearAllWrapper = styled.div`
  display: flex;
  @media only screen and (max-width: 1260px) {
    margin-right: 15px;
  } ;
`;

const ClearAll = styled(Link)`
  text-align: center;
  width: 55px;
`;

const ComparePanelItem = styled.div`
  position: relative;
  display: flex;
  justify-content: center;
  align-items: center;
  box-sizing: border-box;
  width: 210px;
  height: 40px;
  border-radius: 4px;
  border: 1px solid #ccc;
  margin-left: 20px;
  font-size: 11px;
  color: #666;
  cursor: pointer;

  @media screen and (max-width: 1024px) {
    margin-left: 10px;
  }

  &:hover {
    border-color: #4855a0;
  }

  &.dc-compare-panel-item-empty {
    background: #f2f2f2;
    border-style: dashed;
    cursor: default;

    &:hover {
      border-color: #ccc;
    }
  }
`;

const ComparePanelWrapper = styled.div`
  box-sizing: border-box;
  position: fixed;
  bottom: 0;
  left: 0;
  right: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 14px;
  height: 65px;
  background: #fff;
  box-shadow: 0 -3px 9px 0 rgba(0, 0, 0, 0.08);
  z-index: 9998;

  @media ${(p) => p.theme.breakpoints.mobile} {
    display: none;
  }

  ${ComparePanelItem}:first-child() {
    @media screen and (max-width: 1359px) {
      .dc-compare-panel-product__tooltip {
        left: 190px;
      }
    }
    @media screen and (min-width: 1260px) {
      margin: 0 0 0 -25px;
    }
  }
`;

export default class ComparePanel extends PureComponent {
  constructor(props) {
    super(props);

    this.checkBackToTop = this.checkBackToTop.bind(this);
  }

  componentDidMount() {
    this.checkBackToTop();
  }

  componentDidUpdate() {
    this.checkBackToTop();
  }

  checkBackToTop() {
    // check for back to top button on touch devices
    if (!this.props.isDesktop) {
      const backToTop = document.querySelector('[class*="BackToTopButton"]');
      if (backToTop) {
        if (this.props.products.length === 0) {
          backToTop.style.bottom = '20px';
        } else {
          backToTop.style.bottom = '70px';
        }
      }
    }
  }

  isProductOutOfStock(product) {
    const defaultAvailability = product.sponsored
      ? convertDefaultChannelsAvailabilityForSponsoredProduct(product.deliveryOptions)
      : getDefaultDeliveriesAndCollectionForProduct(product);

    return (
      defaultAvailability.showEmailMeBack ||
      (typeof defaultAvailability.delivery !== 'undefined' &&
        defaultAvailability.delivery.type === 'homeDeliveryUnavailable' &&
        typeof defaultAvailability.inStore !== 'undefined' &&
        defaultAvailability.inStore.type === 'collectInStoreUnavailable')
    );
  }

  renderCompareItems() {
    const { products, onClickProductImage, onRemoveProduct, isDesktop } = this.props;

    const translations = {
      fromDateTranslation: uiLocale.getString('component.productlisting.fromDateLabel'),
      toDateTranslation: uiLocale.getString('component.productlisting.toLabel'),
      wasPriceTranslation: uiLocale.getString('component.productlisting.wasLabel'),
      savingsPriceTranslation: uiLocale.getString('component.productlisting.saveLabel')
    };

    const compareItems = [];

    for (let i = 0; i < MAX_NUMBER; i++) {
      const product = products[i] || null;
      const isFirst = i === 0;

      compareItems.push(
        <ComparePanelItem
          className={
            product ? 'dc-compare-panel-item' : 'dc-compare-panel-item dc-compare-panel-item-empty'
          }
          key={product ? product.id : i}
        >
          {product && (
            <CompareProduct
              title={product.title}
              price={product.price}
              isProductOutOfStock={this.isProductOutOfStock(product)}
              url={product.link}
              brand={product.brand.title}
              imageSrc={product.image.src}
              imageAlt={product.image.label}
              onRemove={() => {
                onRemoveProduct(product.id);
              }}
              translations={translations}
              firstChild={isFirst}
              onTooltipClick={() => {
                onClickProductImage(product.id);
              }}
              isDesktop={isDesktop}
            />
          )}
        </ComparePanelItem>
      );
    }

    return compareItems;
  }

  render() {
    const { products, isDesktop, onClearAllButtonClick, onCompareButtonClick } = this.props;

    const translations = {
      compareLabel: uiLocale.getString('component.comparepanel.compare_button'),
      clearAll: uiLocale.getString('component.comparepanel.clearAll')
    };

    const buttonEnabledTranslation = `${translations.compareLabel}${
      isDesktop ? ' products' : ''
    } (${products.length} of 4)`;

    // TODO: add translations
    const buttonDisabledTranslation = 'Add one more product to compare';

    return (
      <div>
        {products.length > 0 && (
          <ComparePanelWrapper>
            {this.renderCompareItems()}
            <CompareButton
              onClick={(e) => {
                e.preventDefault();
                onCompareButtonClick(products);
              }}
              disabled={products.length === 1}
            >
              {products.length > 1 ? buttonEnabledTranslation : buttonDisabledTranslation}
            </CompareButton>
            <ClearAllWrapper>
              <ClearAll
                onClick={(e) => {
                  e.preventDefault();
                  onClearAllButtonClick();
                }}
              >
                {translations.clearAll}
              </ClearAll>
            </ClearAllWrapper>
          </ComparePanelWrapper>
        )}
      </div>
    );
  }
}

ComparePanel.displayName = 'ComparePanel';

ComparePanel.propTypes = {
  isDesktop: PropTypes.bool.isRequired,
  onRemoveProduct: PropTypes.func.isRequired,
  onClearAllButtonClick: PropTypes.func.isRequired,
  onClickProductImage: PropTypes.func.isRequired,
  onCompareButtonClick: PropTypes.func.isRequired,
  products: PropTypes.arrayOf(PropTypes.object)
};

ComparePanel.defaultProps = {
  products: []
};
